export function capitalizeFirst(str) {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getStatusLabel(status) {
  switch (status) {
    case "fine":
      return "Ottimo";
    case "bad":
      return "Critico";
    case "ok":
      return "Discreto";
    default:
      return "-";
  }
}

export function getNdviStatus(ndvi) {
  if (ndvi <= 25) return "bad";
  if (ndvi <= 60) return "ok";
  return "fine";
}

export function getMeasurementKey(measurement) {
  return normalizeMeasurementLabelKey(
    measurement?.measurement_name || measurement?.measurement_type,
  );
}

export function normalizeMeasurementLabelKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

export const DIGITAL_TWIN_FEATURE_LABELS_IT = {
  name: "Nome",
  nome: "Nome",
  ndvi: "NDVI",
  volume: "Volume",
  area: "Area",
  count: "Numero piante",
  height: "Altezza",
  weight: "Peso",
  "weight kg": "Peso (kg)",
  "height at withers": "Altezza al garrese",
  "height at withers cm": "Altezza al garrese (cm)",
  "body length": "Lunghezza corpo",
  "body length cm": "Lunghezza corpo (cm)",
  "body condition score": "Punteggio condizione corporea",
  girth: "Circonferenza toracica",
  "girth cm": "Circonferenza toracica (cm)",
  "hip width": "Larghezza fianchi",
  "hip width cm": "Larghezza fianchi (cm)",
  "milk production": "Produzione latte",
  "milk production l day": "Produzione latte (L/giorno)",
};

export function formatDigitalTwinFeatureLabel(value) {
  const normalizedKey = normalizeMeasurementLabelKey(value);
  const translatedLabel = DIGITAL_TWIN_FEATURE_LABELS_IT[normalizedKey];

  if (translatedLabel) return translatedLabel;

  return normalizedKey
    .split(" ")
    .filter(Boolean)
    .map((word) => capitalizeFirst(word))
    .join(" ");
}

export function getMeasurementLabel(measurement) {
  return formatDigitalTwinFeatureLabel(
    measurement?.measurement_name || measurement?.measurement_type,
  );
}

export function getLatestMeasurementByName(measurements, measurementName) {
  if (!Array.isArray(measurements)) return null;

  const normalizedName = normalizeMeasurementLabelKey(measurementName);

  return measurements
    .filter((measurement) => getMeasurementKey(measurement) === normalizedName)
    .sort((a, b) => {
      const timestampA = new Date(a?.survey_timestamp || 0).getTime();
      const timestampB = new Date(b?.survey_timestamp || 0).getTime();

      if (timestampB !== timestampA) return timestampB - timestampA;

      return (b?.id || 0) - (a?.id || 0);
    })[0] || null;
}

export function getMeasurementTypes(measurements) {
  if (!Array.isArray(measurements)) return [];

  return measurements.reduce((types, measurement) => {
    const key = getMeasurementKey(measurement);

    if (!key || types.some((type) => type.key === key)) {
      return types;
    }

    return [...types, { key, label: getMeasurementLabel(measurement) }];
  }, []);
}

export function getMeasurementHistory(measurements, type) {
  if (!Array.isArray(measurements)) return [];

  const normalizedType = normalizeMeasurementLabelKey(type);

  return measurements
    .filter((measurement) => getMeasurementKey(measurement) === normalizedType)
    .map((measurement) => {
      const timestampMs = new Date(
        measurement?.survey_timestamp || "",
      ).getTime();

      return {
        timestamp: measurement?.survey_timestamp,
        timestampMs,
        rawValue: measurement?.measurement_value,
        value: Number(measurement?.measurement_value),
      };
    })
    .filter(
      (measurement) =>
        measurement.timestamp &&
        measurement.rawValue !== null &&
        measurement.rawValue !== undefined &&
        measurement.rawValue !== "" &&
        !Number.isNaN(measurement.timestampMs) &&
        !Number.isNaN(measurement.value),
    )
    .sort((a, b) => a.timestampMs - b.timestampMs);
}

export function formatDigitalTwinMeasurementValue(value, type) {
  const numericValue = Number(value);

  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(numericValue)
  ) {
    return "-";
  }

  if (normalizeMeasurementLabelKey(type) === "count") {
    return numericValue.toFixed(0);
  }

  return numericValue.toFixed(2);
}

export function getDigitalTwinRecords(digitalTwinData) {
  if (Array.isArray(digitalTwinData)) return digitalTwinData;
  if (Array.isArray(digitalTwinData?.data)) return digitalTwinData.data;
  return [];
}

export function is3DMeshDigitalTwin(digitalTwin) {
  const modelFormat = String(digitalTwin?.model_format || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  return modelFormat === "3dmesh";
}

export function getLatest3DMeshDigitalTwin(digitalTwinData) {
  return getDigitalTwinRecords(digitalTwinData)
    .filter(is3DMeshDigitalTwin)
    .sort((a, b) => {
      const timestampA = new Date(a?.survey_timestamp || 0).getTime();
      const timestampB = new Date(b?.survey_timestamp || 0).getTime();

      if (timestampB !== timestampA) return timestampB - timestampA;

      return (b?.id || 0) - (a?.id || 0);
    })[0] || null;
}

export function getHoursSinceTimestamp(timestamp) {
  if (!timestamp) return null;

  const parsedTimestamp = new Date(timestamp).getTime();
  if (Number.isNaN(parsedTimestamp)) return null;

  return Math.max(0, Math.floor((Date.now() - parsedTimestamp) / 36e5));
}

export function getLatestSurveyTimestamp(items) {
  if (!Array.isArray(items)) return null;

  return items
    .map((item) => item?.survey_timestamp)
    .filter(Boolean)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0] || null;
}

export function formatRelativeTime(timestamp) {
  if (!timestamp) return "-";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";

  const diffMs = date.getTime() - Date.now();
  const absDiffMs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat("it-IT", { numeric: "auto" });

  if (absDiffMs < 60 * 1000) {
    return rtf.format(Math.round(diffMs / 1000), "second");
  }
  if (absDiffMs < 60 * 60 * 1000) {
    return rtf.format(Math.round(diffMs / (60 * 1000)), "minute");
  }
  if (absDiffMs < 24 * 60 * 60 * 1000) {
    return rtf.format(Math.round(diffMs / (60 * 60 * 1000)), "hour");
  }
  if (absDiffMs < 30 * 24 * 60 * 60 * 1000) {
    return rtf.format(Math.round(diffMs / (24 * 60 * 60 * 1000)), "day");
  }

  return date.toLocaleDateString("it-IT");
}

export async function handleDownload(response, onProgress, onFileReady, onError) {
  try {
    if (!response.ok) {
      throw new Error(`Errore di rete: ${response.status}`);
    }

    const reader = response.body.getReader();
    const contentLength = +response.headers.get('Content-Length') || 0;
    
    let receivedLength = 0;
    let chunks = []; 
    
    while(true) {
      const { done, value } = await reader.read();
      if (done) break;
      
      chunks.push(value);
      receivedLength += value.length;
      
      if (contentLength && typeof onProgress === 'function') {
        const progress = Math.round((receivedLength / contentLength) * 100);
        onProgress(progress);
      }
    }

    const allChunks = new Uint8Array(receivedLength);
    let position = 0;
    for (let chunk of chunks) {
      allChunks.set(chunk, position);
      position += chunk.length;
    }

    const fileBlob = new Blob([allChunks], { type: 'application/octet-stream' });
    const fileUrl = window.URL.createObjectURL(fileBlob);
    
    if (typeof onFileReady === 'function') {
      onFileReady({
        blob: fileBlob,
        url: fileUrl,
        arrayBuffer: allChunks.buffer
      });
    }

  } catch (err) {
    if (typeof onError === 'function') {
      onError(err.message);
    }
  }
}

export function isPmtilesRaster(raster) {
  const rasterType = String(raster?.raster_type || "").toLowerCase();
  const rasterFile = String(raster?.raster_file || raster?.file_name || "").toLowerCase();

  return (
    rasterType.includes("pmtiles") ||
    rasterFile.endsWith(".pmtiles")
  );
}

export function isGeoTiffRaster(raster) {
  const rasterType = String(raster?.raster_type || "").toLowerCase();
  const rasterFile = String(raster?.raster_file || raster?.file_name || "").toLowerCase();

  return (
    rasterType.includes("cog") ||
    rasterType.includes("geotiff") ||
    rasterType.includes("geo_tiff") ||
    rasterType.includes("tiff") ||
    rasterFile.endsWith(".tif") ||
    rasterFile.endsWith(".tiff")
  );
}

export function getRasterBase64Payload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") return payload;

  const candidate =
    payload.url ||
    payload.file_url ||
    payload.download_url ||
    payload.pmtiles ||
    payload.file ||
    payload.content ||
    payload.raster_base64 ||
    payload.raster_file ||
    payload.file_base64 ||
    payload.base64;

  if (typeof candidate === "string") return candidate;

  if (payload.data && typeof payload.data === "object") {
    return getRasterBase64Payload(payload.data);
  }

  return null;
}

export function getDigitalTwinModelBase64Payload(payload) {
  if (!payload) return null;
  if (typeof payload === "string") return payload;

  const candidate =
    payload.model_base64 ||
    payload.model ||
    payload.file ||
    payload.content ||
    payload.file_base64 ||
    payload.base64;

  if (typeof candidate === "string") return candidate;

  if (payload.data && typeof payload.data === "object") {
    return getDigitalTwinModelBase64Payload(payload.data);
  }

  return null;
}

const DEFAULT_RASTER_BUCKET_URL = "https://smaart-data-stg.s3.amazonaws.com/";

function createPublicDataUrl(filePath, allowedExtensions, bucketUrl) {
  if (!filePath || typeof filePath !== "string") return null;

  const trimmedFilePath = filePath.trim();

  if (
    trimmedFilePath.startsWith("http://") ||
    trimmedFilePath.startsWith("https://")
  ) {
    return trimmedFilePath;
  }

  if (
    trimmedFilePath.startsWith("data:") ||
    trimmedFilePath.startsWith("blob:")
  ) {
    return null;
  }

  const hasAllowedExtension = allowedExtensions.some((extension) =>
    trimmedFilePath.toLowerCase().endsWith(extension),
  );
  const isPublicDataPath = trimmedFilePath.includes("/") || hasAllowedExtension;

  if (!isPublicDataPath) return null;

  const normalizedBucketUrl = bucketUrl.endsWith("/")
    ? bucketUrl
    : `${bucketUrl}/`;
  const normalizedFilePath = trimmedFilePath.replace(/^\/+/, "");
  const encodedFilePath = normalizedFilePath
    .split("/")
    .map((segment) => encodeURIComponent(segment))
    .join("/");

  return `${normalizedBucketUrl}${encodedFilePath}`;
}

export function createPublicRasterUrl(rasterFile) {
  return createPublicDataUrl(
    rasterFile,
    [".pmtiles", ".tif", ".tiff"],
    process.env.REACT_APP_RASTER_BUCKET_URL || DEFAULT_RASTER_BUCKET_URL,
  );
}

export function createPublicDigitalTwinModelUrl(modelFile) {
  return createPublicDataUrl(
    modelFile,
    [
      ".twin",
      ".glb",
      ".gltf",
      ".obj",
      ".ply",
    ],
    process.env.REACT_APP_DIGITAL_TWIN_BUCKET_URL ||
      process.env.REACT_APP_RASTER_BUCKET_URL ||
      DEFAULT_RASTER_BUCKET_URL,
  );
}

function isLikelyBase64(value) {
  if (!value || typeof value !== "string") return false;

  const cleanValue = value.trim();
  if (cleanValue.length < 32) return false;
  if (cleanValue.length % 4 !== 0) return false;

  return /^[A-Za-z0-9+/]+={0,2}$/.test(cleanValue);
}

function decodeBase64ToBytes(base64Payload) {
  const binaryString = window.atob(base64Payload);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

function getBytesAsciiPrefix(bytes, length = 64) {
  return Array.from(bytes.slice(0, length))
    .map((byte) => String.fromCharCode(byte))
    .join("");
}

export function get3DModelPayloadFormat(bytes) {
  if (!bytes?.length) return "unknown";

  const prefix = getBytesAsciiPrefix(bytes);
  const trimmedPrefix = prefix.trimStart();

  if (prefix.startsWith("glTF")) return "glb";
  if (trimmedPrefix.startsWith("{") && trimmedPrefix.includes('"asset"')) {
    return "gltf";
  }
  if (trimmedPrefix.toLowerCase().startsWith("ply")) return "ply";
  if (/^(#.*\n)?\s*(v|o|g)\s/m.test(prefix)) return "obj";

  return "unknown";
}

export function isGltfCompatible3DModelFormat(modelPayloadFormat) {
  return modelPayloadFormat === "glb" || modelPayloadFormat === "gltf";
}

export function createPmtilesObjectUrlFromBase64(base64Payload) {
  if (!base64Payload || typeof base64Payload !== "string") return null;

  const trimmedPayload = base64Payload.trim();

  if (
    trimmedPayload.startsWith("http://") ||
    trimmedPayload.startsWith("https://") ||
    trimmedPayload.startsWith("blob:") ||
    trimmedPayload.startsWith("/")
  ) {
    return {
      url: trimmedPayload,
      shouldRevoke: false,
    };
  }

  const dataUrlMatch = trimmedPayload.match(/^data:([^;]+);base64,(.*)$/);
  const mimeType = dataUrlMatch?.[1] || "application/octet-stream";
  const cleanBase64 = dataUrlMatch ? dataUrlMatch[2] : trimmedPayload;

  if (!dataUrlMatch && !isLikelyBase64(cleanBase64)) {
    return null;
  }

  const bytes = decodeBase64ToBytes(cleanBase64);

  const blob = new Blob([bytes], { type: mimeType });

  return {
    blob,
    url: window.URL.createObjectURL(blob),
    arrayBuffer: bytes.buffer,
    sourceKey: `raster-${Date.now()}.pmtiles`,
    shouldRevoke: true,
  };
}

export function createDigitalTwinModelObjectUrlFromBase64(base64Payload, modelFile) {
  if (!base64Payload || typeof base64Payload !== "string") return null;

  const trimmedPayload = base64Payload.trim();

  if (
    trimmedPayload.startsWith("http://") ||
    trimmedPayload.startsWith("https://") ||
    trimmedPayload.startsWith("blob:") ||
    trimmedPayload.startsWith("/")
  ) {
    return {
      url: trimmedPayload,
      shouldRevoke: false,
    };
  }

  const dataUrlMatch = trimmedPayload.match(/^data:([^;]+);base64,(.*)$/);
  const mimeType = dataUrlMatch?.[1] || "model/gltf-binary";
  const cleanBase64 = dataUrlMatch ? dataUrlMatch[2] : trimmedPayload;

  if (!dataUrlMatch && !isLikelyBase64(cleanBase64)) {
    return null;
  }

  const bytes = decodeBase64ToBytes(cleanBase64);
  const modelPayloadFormat = get3DModelPayloadFormat(bytes);

  if (!isGltfCompatible3DModelFormat(modelPayloadFormat)) {
    return {
      arrayBuffer: bytes.buffer,
      format: modelPayloadFormat,
      isCompatible: false,
      shouldRevoke: false,
    };
  }

  const blob = new Blob([bytes], { type: mimeType });

  return {
    blob,
    url: window.URL.createObjectURL(blob),
    arrayBuffer: bytes.buffer,
    format: modelPayloadFormat,
    isCompatible: true,
    sourceKey: modelFile || `digital-twin-model-${Date.now()}.twin`,
    shouldRevoke: true,
  };
}
